import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Card,
  CardContent,
  Paper,
  Skeleton,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import RequestCard from './RequestCard';
import RequestReviewDialog from './RequestReviewDialog';
import {
  fetchRequests,
  fetchRequestReviewContext,
  approveRequest,
  rejectRequest,
  cancelRequest,
} from '../Fieldwork/api';

const Requests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewContext, setReviewContext] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContext, setLoadingContext] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Date range state - default to last 3 months (90 days)
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setDate(today.getDate() - 90);

    return {
      from: threeMonthsAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  });

  const requestTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'partial_withdrawal', label: 'Partial Withdrawal' },
    { value: 'full_withdrawal', label: 'Full Withdrawal' },
    { value: 'account_offset', label: 'Account Offset' },
    { value: 'account_deactivation', label: 'Account Deactivation' },
    { value: 'savings_transfer', label: 'Savings Transfer' },
    { value: 'group_transfer', label: 'Group Transfer' },
  ];

  // Fetch requests from API
  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        from_date: dateRange.from,
        to_date: dateRange.to,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.request_type = typeFilter;
      }

      const data = await fetchRequests(params);

      // Handle both paginated and non-paginated responses
      const transformedRequests = data.results || data;
      setRequests(Array.isArray(transformedRequests) ? transformedRequests : []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.message || 'Failed to load requests');
      setSnackbar({
        open: true,
        message: 'Failed to load requests',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Load requests on mount and when filters change
  useEffect(() => {
    loadRequests();
  }, [dateRange, statusFilter, typeFilter]);

  // Filter requests locally for search
  const filteredRequests = requests.filter((request) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      request.member_name?.toLowerCase().includes(searchLower) ||
      request.member_id_display?.toLowerCase().includes(searchLower) ||
      request.request_id?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate counts
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;
  const executedCount = requests.filter(r => r.status === 'executed').length;

  const handleReviewClick = async (request) => {
    setSelectedRequest(request);

    // Fetch review context
    try {
      setLoadingContext(true);
      const context = await fetchRequestReviewContext(request.id);
      setReviewContext(context);
    } catch (err) {
      console.error('Error fetching review context:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load request details',
        severity: 'error',
      });
    } finally {
      setLoadingContext(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedRequest(null);
    setReviewContext(null);
  };

  const handleApprove = async (requestId, notes) => {
    try {
      await approveRequest(requestId, notes);

      setSnackbar({
        open: true,
        message: 'Request approved successfully',
        severity: 'success',
      });

      // Reload requests
      loadRequests();
      handleCloseDialog();
    } catch (err) {
      console.error('Error approving request:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to approve request',
        severity: 'error',
      });
    }
  };

  const handleReject = async (requestId, reason) => {
    try {
      await rejectRequest(requestId, reason);

      setSnackbar({
        open: true,
        message: 'Request rejected',
        severity: 'success',
      });

      // Reload requests
      loadRequests();
      handleCloseDialog();
    } catch (err) {
      console.error('Error rejecting request:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to reject request',
        severity: 'error',
      });
    }
  };

  const handleCancel = async (requestId, reason) => {
    try {
      await cancelRequest(requestId, reason);

      setSnackbar({
        open: true,
        message: 'Request cancelled',
        severity: 'success',
      });

      // Reload requests
      loadRequests();
      handleCloseDialog();
    } catch (err) {
      console.error('Error cancelling request:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to cancel request',
        severity: 'error',
      });
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Transform request data for RequestCard component
  const transformRequestForCard = (request) => ({
    id: request.id,
    type: request.request_type,
    memberName: request.member_name,
    memberNumber: request.member_id_display,
    groupName: request.member_group || '',
    currentGroup: request.member_group || '',
    amount: request.requested_amount || 0,
    currentBalance: request.requested_amount || 0, // Placeholder, actual from context
    loanBalance: 0, // From context
    offsetAmount: request.requested_amount || 0,
    savingsBalance: 0, // From context
    fromAccount: request.additional_data?.from_account || '',
    toAccount: request.additional_data?.to_account || '',
    requestedGroup: request.additional_data?.new_group_name || '',
    reason: request.reason,
    requestDate: request.created_at,
    status: request.status,
    fieldOfficer: request.created_by_name || 'Unknown',
    approvalDate: request.reviewed_at,
    approvalNotes: request.review_notes,
    rejectionDate: request.reviewed_at,
    rejectionReason: request.review_notes,
  });

  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search by member, number, or request ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status ({requests.length})</MenuItem>
                <MenuItem value="pending">Pending ({pendingCount})</MenuItem>
                <MenuItem value="approved">Approved ({approvedCount})</MenuItem>
                <MenuItem value="rejected">Rejected ({rejectedCount})</MenuItem>
                <MenuItem value="executed">Executed ({executedCount})</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Request Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Request Type"
              >
                {requestTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              size="small"
              value={dateRange.from}
              onChange={(e) => handleDateRangeChange('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              size="small"
              value={dateRange.to}
              onChange={(e) => handleDateRangeChange('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Spacer between the toolbar and the results below, colored to
          match the page background so it reads clearly as empty space
          against the Paper panels on either side */}
      <Box sx={{ height: 50, backgroundColor: 'background.default' }} />

      {/* Content area */}
      <Box>
      {/* Loading */}
      {loading && (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" width="100%" height={180} />
                  <Skeleton width="60%" sx={{ mt: 2 }} />
                  <Skeleton width="40%" />
                  <Skeleton width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadRequests}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Request Cards Grid */}
      {!loading && !error && (
        <Grid container spacing={3}>
          {filteredRequests.map((request) => (
            <Grid item xs={12} md={6} lg={4} key={request.id}>
              <RequestCard
                request={transformRequestForCard(request)}
                onReviewClick={() => handleReviewClick(request)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && !error && filteredRequests.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <InboxIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No requests found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      )}

      </Box>

      {/* Review Dialog */}
      <RequestReviewDialog
        request={selectedRequest ? transformRequestForCard(selectedRequest) : null}
        reviewContext={reviewContext}
        loadingContext={loadingContext}
        open={Boolean(selectedRequest)}
        onClose={handleCloseDialog}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Requests;